export type UserProfileResult =
  /** The profile JSON, on a 2xx response. */
  | Record<string, unknown>
  /** The backend rejected the session token (401/403) — the user may not view exams. */
  | 'unauthorized'
  /** No URL / no token / network error / non-2xx / invalid JSON — treat as "no profile". */
  | null;

/**
 * Fetches the physician/user profile from the RIS backend
 * (`window.PROFILE_API_URL`) using the session's `Authorization: Bearer <token>`
 * header — the same token received in the viewer URL and already installed on
 * `userAuthenticationService` by `updateAuthServiceAndCleanUrl`. The first
 * requested study UID is passed as a `?studyInstanceUID=<uid>` query param
 * (only single-study viewing is supported for now).
 *
 * Never rejects. A 401/403 resolves to `'unauthorized'` (the caller redirects to
 * the 404 page); any other failure (missing URL, no auth header, network error,
 * other non-2xx, invalid JSON) resolves to `null` so the viewer can still load
 * without the profile.
 */
export async function fetchUserProfile(
  userAuthenticationService,
  studyInstanceUIDs: string[] = []
): Promise<UserProfileResult> {
  const baseUrl = (window as any).PROFILE_API_URL;

  if (!baseUrl) {
    return null;
  }

  const authHeader = userAuthenticationService?.getAuthorizationHeader?.();

  if (!authHeader?.Authorization) {
    // No session token (e.g. a manual page reload dropped the URL token).
    return null;
  }

  // Only single-study viewing is supported for now — send the first UID as
  // `?studyInstanceUID=<uid>`.
  const url = new URL(baseUrl);
  const [studyInstanceUID] = studyInstanceUIDs.filter(Boolean);
  if (studyInstanceUID) {
    url.searchParams.set('studyInstanceUID', studyInstanceUID);
  }

  try {
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        ...authHeader,
      },
    });

    if (response.status === 401 || response.status === 403) {
      return 'unauthorized';
    }

    if (response.status < 200 || response.status >= 300) {
      console.warn('fetchUserProfile: unexpected status', response.status);
      return null;
    }

    return await response.json();
  } catch (error) {
    console.warn('fetchUserProfile: request failed', error);
    return null;
  }
}

export default fetchUserProfile;
